// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { parseSchema } from "@utils/schema";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import type {
  DynamicQuestion,
  DynamicQuestionContent,
} from "@modules/service/reducer/dynamic-questions/types";
import * as accountSelectors from "@modules/account/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { QuestionTypeEnum } from "@modules/service/constants";
import Step1QuestionType from "./step1-question-type";
import Step3ReasonSpecificUsages from "./step3-reason-specific-usages";
import Step4LengthScope from "./step4-length-scope";
import StepStaffGroups from "./step-staff-groups";
import AnswerOptions from "./answer-options";

type PropsFromState = $Exact<{
  isFirstCareUser: boolean,
  dynamicQuestionContainer: ?DynamicQuestion,
  draft: ?DynamicQuestionContent,
  isValid: boolean,
  schema: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  setShowEditModal: typeof actionCreators.ShowDynamicQuestionEditModal,
  updateDynamicQuestion: typeof actionCreators.UpdateDynamicQuestion,
  getAbsenceReasons: typeof sharedActionCreators.GetAbsenceReasons,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  setDynamicQuestionRevisionSchema: typeof actionCreators.SetDynamicQuestionRevisionSchema,
}>;

type OwnProps = $Exact<{
  clientId: number,
  defaultStep: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EditDynamicQuestionModal extends Component<Props> {
  componentDidMount = async () => {
    if (this.props.getAbsenceReasons) {
      this.props.getAbsenceReasons({ clientId: this.props.clientId });
    }

    this.props.getHierarchyTree({
      clientId: this.props.clientId,
    });
    const schema = await parseSchema("DynamicQuestionRevisionDTO", {
      clientId: this.props.clientId,
    });

    if (this.props.setDynamicQuestionRevisionSchema) {
      this.props.setDynamicQuestionRevisionSchema(schema);
    }
  };

  handleCancel = () => {
    this.props.setShowEditModal({ show: false });
  };

  handleSubmitDynamicQuestionDraft = () => {
    if (this.props.dynamicQuestionContainer && this.props.draft) {
      let draft = {
        ...this.props.draft,
        advisorType: "ALL",
        startNotification: false,
      };

      if (this.isAnswerOptionsHidden) {
        draft = {
          ...draft,
          selectableValues: [],
        };
      }

      this.props.updateDynamicQuestion({
        clientId: this.props.clientId,
        stableId: this.props.dynamicQuestionContainer.id,
        draft,
      });
    }
  };

  get isAnswerOptionsHidden() {
    const quetionType = this.props.draft && this.props.draft?.questionType;
    return quetionType === QuestionTypeEnum.DATE_PICK || quetionType === QuestionTypeEnum.FREE_TEXT;
  }

  get totalSteps() {
    return this.isAnswerOptionsHidden ? 4 : 5;
  }

  render() {
    if (!this.props.schema) {
      return null;
    }
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EDIT_DYNAMIC_QUESTION_MODAL_TITLE", {
            providerId: (this.props.dynamicQuestionContainer || {}).id || " ",
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <CollapsibleWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step
              component={Step1QuestionType}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_1_TITLE", {
                step: 1,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={Step3ReasonSpecificUsages}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_3_TITLE", {
                step: 2,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={StepStaffGroups}
              title={this.props.t("STAFF_GROUPS_STEP_TITLE", {
                step: 3,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={Step4LengthScope}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_4_TITLE", {
                step: 4,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={AnswerOptions}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_5_TITLE", {
                step: 5,
                totalSteps: this.totalSteps,
              })}
              hidden={this.isAnswerOptionsHidden}
            />
          </CollapsibleWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            onClick={this.handleSubmitDynamicQuestionDraft}
            disabled={!this.props.isValid}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    isFirstCareUser: accountSelectors.isFirstCareUser(state),
    dynamicQuestionContainer: serviceSelectors.getDynamicQuestionContainer(state),
    draft: serviceSelectors.getDynamicQuestionDraft(state),
    isValid: serviceSelectors.isDynamicQuestionDraftValid(state),
    schema: serviceSelectors.getDynamicQuestionRevisionSchema(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setShowEditModal: actionCreators.ShowDynamicQuestionEditModal,
  updateDynamicQuestion: actionCreators.UpdateDynamicQuestion,
  getAbsenceReasons: sharedActionCreators.GetAbsenceReasons,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  setDynamicQuestionRevisionSchema: actionCreators.SetDynamicQuestionRevisionSchema,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EditDynamicQuestionModal);
